import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import Notification from "../../components/UI/Notification";
import { getToken } from "../../utils/tokenUtils";
import ReactPaginate from 'react-paginate';
import { 
  X, 
  Calendar, 
  Clock, 
  MoreVertical, 
  Eye, 
  Check, 
  MinusCircle,
  User,
  RefreshCw,
  FileText
} from "lucide-react";
import { FaPlus, FaSignOutAlt } from "react-icons/fa";
import { getRoleBasedRoute } from '../../utils/routeUtils';
import { AuthContext } from "../../contexts/AuthContext";
import StatusBadge from '../../components/UI/StatusBadge';

const STOApproval = () => {
  const [requests, setRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [notification, setNotification] = useState(null);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const token = getToken();
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    perPage: 15,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilters, setActiveFilters] = useState({});
  const [filteredData, setFilteredData] = useState([]);
  const [isFiltering, setIsFiltering] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [actionType, setActionType] = useState(""); // "approved" or "rejected"
  const [approverComments, setApproverComments] = useState("");

  const { roles } = useContext(AuthContext);
  const getRoleBasedRouteLocal = (route) => getRoleBasedRoute(route, roles);

  const handleFilterChange = (field, value) => {
    setActiveFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const clearFilters = () => {
    setActiveFilters({});
    setSearchQuery("");
  };

  useEffect(() => {
    fetchRequests();
  }, [token]);

  useEffect(() => {
    let result = requests;

    if (searchQuery) {
      const lowercasedQuery = searchQuery.toLowerCase();
      result = result.filter((item) => {
        return (
          (item.user?.name?.toLowerCase().includes(lowercasedQuery)) ||
          (item.requestable?.request_date?.toLowerCase().includes(lowercasedQuery)) ||
          (item.requestable?.request_type?.toLowerCase().includes(lowercasedQuery)) ||
          (item.requestable?.remark?.toLowerCase().includes(lowercasedQuery)) ||
          (item.status?.toLowerCase().includes(lowercasedQuery))
        );
      });
    }

    Object.entries(activeFilters).forEach(([field, filterValue]) => {
      if (filterValue) {
        result = result.filter(item => {
          const value = item[field];
          return value?.toString().toLowerCase().includes(filterValue.toLowerCase());
        });
      }
    });

    const filteringNow = searchQuery.length > 0 || Object.values(activeFilters).some(v => !!v);
    setIsFiltering(filteringNow);
    setFilteredData(result);
  }, [searchQuery, activeFilters, requests]);

  const fetchRequests = async (page = 1) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `https://hrmspayroll.prepstripe.com/api/approvals?page=${page}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const data = response.data;
      // Filter only STO requests
      const stoData = (data?.data || []).filter(item => 
        item.requestable_type?.includes('StoRequest') || 
        item.requestable_type?.includes('ShortTimeOff')
      );
      
      setRequests(stoData);
      setLoading(false);
      setPagination({
        currentPage: data?.current_page || 1,
        totalPages: data?.last_page || 1,
        totalItems: data?.total || 0,
        perPage: data?.per_page || 10,
      });

    } catch (error) {
      console.error("Error fetching STO requests", error);
      setNotification({
        message: error?.response?.data?.error || "Failed to fetch STO requests",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleApproveReject = async (id, status) => {
    try {
      const response = await axios.put(
        `https://hrmspayroll.prepstripe.com/api/approvals/${id}`,
        { status, approver_comments: approverComments },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      fetchRequests();
      setIsDetailModalOpen(false);
      setOpenDropdownId(null);
      setNotification({
        message: "Request status updated successfully",
        type: "success",
      });
      setApproverComments("");
    } catch (error) {
      console.error("Error updating status", error);
      const errorMsg = error?.response?.data?.error || error.message || "An error occurred";
      setNotification({
        message: errorMsg,
        type: "error",
      });
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setCurrentPage(newPage);
      fetchRequests(newPage);
    }
  };

  return (
    <div className="px-4 py-2 bg-white dark:bg-gray-900 font-poppins rounded-lg">
      
      {notification && (
        <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />
      )}

      {/* Breadcrumb */}
      <nav className="flex flex-wrap mb-4" aria-label="Breadcrumb">
        <ol className="inline-flex items-center space-x-1 md:space-x-2 rtl:space-x-reverse">
          <li>
            <div className="flex items-center">
              <Link
                to={getRoleBasedRouteLocal("/admin/attendance_management")}
                className="text-sm sm:text-md font-medium text-black hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-500"
              >
                Attendance Management
              </Link>
            </div>
          </li>
          <li aria-current="page">
            <div className="flex items-center">
              <svg className="w-3 h-3 text-gray-400 mx-1" aria-hidden="true" fill="none" viewBox="0 0 6 10">
                <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 9 4-4-4-4"/>
              </svg>
              <span className="ms-1 text-sm sm:text-md font-medium text-gray-500 md:ms-2 dark:text-gray-400">STO Approval</span>
            </div>
          </li>
        </ol>
      </nav>

      <h2 className="text-lg sm:text-xl dark:text-white font-semibold mb-4">STO Approval Requests</h2>

      {/* Search and filter controls */}
      <div className="flex flex-col sm:flex-row gap-4 justify-start items-center mb-6">
        <div className="relative w-full sm:w-80">
          <input
            type="text"
            placeholder="Search by name, date, or type..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full dark:bg-black dark:text-white dark:border-gray-700 placeholder:text-xs md:placeholder:text-sm p-2.5 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              title="Clear search"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="w-full sm:w-48">
          <select
            value={activeFilters.status || ""}
            onChange={(e) => handleFilterChange("status", e.target.value)}
            className="w-full dark:bg-black dark:text-white dark:border-gray-700 p-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
          >
            <option value="">All Status</option>
            <option value="approved">Approved</option>
            <option value="pending">Pending</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>

        {isFiltering && (
          <button
            onClick={clearFilters}
            className="text-sm text-red-500 hover:text-red-700 font-medium hover:underline transition-colors"
          >
            Clear All
          </button>
        )}
      </div>

      <div className="bg-white dark:bg-gray-900 shadow-md rounded-lg overflow-visible">
        <table className="min-w-full table-auto md:table-fixed text-sm">
          <thead className="bg-gray-100 dark:bg-gray-800 dark:text-white">
            <tr className="text-sm sm:text-base">
              <th className="py-2 px-4 text-center font-semibold text-xs md:text-sm whitespace-nowrap">Sr No.</th>
              <th className="py-2 px-4 text-center font-semibold text-xs md:text-sm whitespace-nowrap">Employee</th>
              <th className="py-2 px-4 text-center font-semibold text-xs md:text-sm whitespace-nowrap">Request Date</th>
              <th className="py-2 px-4 text-center font-semibold text-xs md:text-sm whitespace-nowrap">Type</th>
              <th className="py-2 px-4 text-center font-semibold text-xs md:text-sm whitespace-nowrap">From - To</th>
              <th className="py-2 px-4 text-center font-semibold text-xs md:text-sm whitespace-nowrap">Status</th>
              <th className="py-2 px-4 text-center font-semibold text-xs md:text-sm whitespace-nowrap">Actions</th>
            </tr>
          </thead>
          <tbody>
          {loading ? (
            <tr className="border-b">
              <td colSpan={7} className="py-8">
                <div className="flex flex-col items-center justify-center gap-3">
                  <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
                  <p className="text-gray-500 dark:text-gray-400 font-medium">Loading requests...</p>
                </div>
              </td>
            </tr>
          ) : 
          filteredData.length === 0 ? (
            <tr>
              <td colSpan="7" className="text-center py-8">
                <div className="flex flex-col items-center justify-center gap-3">
                  <FaSignOutAlt className="w-12 h-12 text-gray-300 dark:text-gray-600" />
                  <p className="text-gray-500 dark:text-gray-400 font-medium">No STO requests found</p>
                </div>
              </td>
            </tr>
          ) : (
            filteredData.map((req, index) => (
              <tr key={req.id} className="border-b dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-950 transition-colors">
                <td className="text-center py-2 px-4 text-xs md:text-sm font-medium">
                  {(pagination.currentPage - 1) * pagination.perPage + index + 1}
                </td>
                <td className="text-center py-2 px-4 text-xs md:text-sm">{req.user?.name || 'N/A'}</td>
                <td className="text-center py-2 px-4 text-xs md:text-sm">{req.requestable?.request_date || 'N/A'}</td>
                <td className="text-center py-2 px-4 text-xs md:text-sm capitalize">{req.requestable?.request_type || 'N/A'}</td>
                <td className="text-center py-2 px-4 text-xs md:text-sm font-semibold">
                  {req.requestable?.from_time} - {req.requestable?.to_time}
                </td>
                <td className="text-center py-2 px-4 text-xs md:text-sm">
                  <StatusBadge status={req?.status} />
                </td>
                <td className="text-center py-2 px-4">
                  <div className="relative inline-block text-left">
                    <button 
                      onClick={() => setOpenDropdownId(openDropdownId === req.id ? null : req.id)}
                      className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full transition-colors flex items-center justify-center mx-auto"
                    >
                      <MoreVertical size={20} />
                    </button>
                  
                    {openDropdownId === req.id && (
                      <>
                        <div className="fixed inset-0 z-10" onClick={() => setOpenDropdownId(null)}></div>
                        <div className={`absolute right-0 ${(index >= filteredData.length - 2 && index > 2) ? 'bottom-full mb-2' : 'top-full mt-2'} w-48 bg-white dark:bg-gray-800 rounded-lg shadow-2xl border dark:border-gray-700 z-20 py-1 overflow-hidden animate-in fade-in slide-in-from-top-1 duration-150`}>
                          <button
                            onClick={() => {
                              setSelectedRequest(req);
                              setIsDetailModalOpen(true);
                              setOpenDropdownId(null);
                            }}
                            className="w-full flex items-center px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                          >
                            <Eye size={16} className="mr-3 text-blue-500" />
                            View Details
                          </button>
                          
                          {req.status === "pending" && (
                            <>
                              <button
                                onClick={() => {
                                  setSelectedRequest(req);
                                  setActionType("approved");
                                  setIsModalOpen(true);
                                  setOpenDropdownId(null);
                                }}
                                className="w-full flex items-center px-4 py-2.5 text-sm text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/30 transition-colors"
                              >
                                <Check size={16} className="mr-3" />
                                Approve
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedRequest(req);
                                  setActionType("rejected");
                                  setIsModalOpen(true);
                                  setOpenDropdownId(null);
                                }}
                                className="w-full flex items-center px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
                              >
                                <MinusCircle size={16} className="mr-3" />
                                Reject
                              </button>
                            </>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))
          )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!isFiltering && filteredData?.length > 0 && (
        <ReactPaginate
          breakLabel="..."
          nextLabel="Next >"
          onPageChange={(event) => handlePageChange(event.selected + 1)}
          forcePage={currentPage - 1}
          pageRangeDisplayed={5}
          pageCount={pagination.totalPages}
          previousLabel="< Prev"
          previousClassName="border rounded-xl bg-white dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 transition-all shadow-sm"
          previousLinkClassName="block px-4 py-2 text-gray-600 dark:text-gray-400 font-medium"
          nextClassName="border rounded-xl bg-white dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 transition-all shadow-sm"
          nextLinkClassName="block px-4 py-2 text-gray-600 dark:text-gray-400 font-medium"
          containerClassName="flex items-center justify-center mt-8 gap-3 font-poppins"
          pageClassName="border rounded-xl bg-white dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 transition-all shadow-sm overflow-hidden"
          pageLinkClassName="block px-4 py-2 text-gray-600 dark:text-gray-400 font-medium"
          activeClassName="!bg-blue-500 !border-blue-500 shadow-blue-200"
          activeLinkClassName="!text-white"
          disabledClassName="opacity-40 cursor-not-allowed"
          disabledLinkClassName="cursor-not-allowed"
        />
      )}

      {/* Detail Modal */}
      {isDetailModalOpen && selectedRequest && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[1000] p-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                    <FaSignOutAlt className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl md:text-2xl font-semibold text-white">
                    STO Request Details
                  </h3>
                </div>
                <button onClick={() => setIsDetailModalOpen(false)} className="p-2 hover:bg-white/20 rounded-lg transition-colors">
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="mb-6 flex justify-center">
                <StatusBadge status={selectedRequest.status} />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <DetailCard label="Employee Name" value={selectedRequest.user?.name} icon={<User className="w-5 h-5 text-blue-600" />} />
                <DetailCard label="Request Date" value={selectedRequest.requestable?.request_date} icon={<Calendar className="w-5 h-5 text-green-600" />} />
                <DetailCard label="Request Type" value={selectedRequest.requestable?.request_type} icon={<FileText className="w-5 h-5 text-orange-600" />} />
                <DetailCard label="Duration" value={`${selectedRequest.requestable?.from_time} - ${selectedRequest.requestable?.to_time} (${selectedRequest.requestable?.minutes} mins)`} icon={<Clock className="w-5 h-5 text-purple-600" />} />
              </div>

              <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 mb-6">
                <h4 className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-2">Remark</h4>
                <p className="text-gray-800 dark:text-gray-200 leading-relaxed">
                  {selectedRequest.requestable?.remark || 'No remark provided'}
                </p>
              </div>

              {selectedRequest.approver_comments && (
                <div className="border-t dark:border-gray-700 pt-6">
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4">
                    <h5 className="text-sm font-semibold text-blue-600 dark:text-blue-400 mb-2">Approver's Comment</h5>
                    <p className="text-gray-800 dark:text-gray-200">{selectedRequest.approver_comments}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900/50 border-t dark:border-gray-800 flex justify-end gap-3">
              <button onClick={() => setIsDetailModalOpen(false)} className="px-6 py-2.5 rounded-xl border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">Close</button>
              
              {selectedRequest.status === "pending" && (
                <>
                  <button onClick={() => { setActionType("rejected"); setIsModalOpen(true); }} className="px-6 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white font-medium transition-all shadow-lg flex items-center">
                    <MinusCircle size={18} className="mr-2" /> Reject
                  </button>
                  <button onClick={() => { setActionType("approved"); setIsModalOpen(true); }} className="px-6 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-medium transition-all shadow-lg flex items-center">
                    <Check size={18} className="mr-2" /> Approve
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Approve/Reject Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[1100] p-4 animate-in fade-in duration-300">
          <div className="bg-white dark:bg-gray-900 rounded-[1.5rem] shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in duration-300">
            <div className={`px-6 py-5 flex items-center gap-4 ${actionType === "approved" ? "bg-[#F0FDF4] dark:bg-green-900/20" : "bg-[#FEF2F2] dark:bg-red-900/20"}`}>
              <div className={`w-12 h-12 rounded-full flex items-center justify-center border-4 ${actionType === "approved" ? "bg-white dark:bg-green-900/40 border-[#DCFCE7] text-[#10B981]" : "bg-white dark:bg-red-900/40 border-[#FEE2E2] text-[#EF4444]"}`}>
                {actionType === "approved" ? <Check size={24} strokeWidth={3} /> : <X size={24} strokeWidth={3} />}
              </div>
              <div>
                <h3 className="text-xl font-bold text-[#1E293B] dark:text-white leading-tight">{actionType === "approved" ? "Approved Request" : "Rejected Request"}</h3>
                <p className="text-xs text-[#64748B] dark:text-gray-400 font-medium">Employee Request</p>
              </div>
            </div>

            <div className="p-6">
              <label htmlFor="approver-comments" className="block text-[10px] font-bold text-[#475569] dark:text-gray-400 uppercase tracking-[0.2em] mb-3">MANAGER REMARK</label>
              <textarea
                id="approver-comments"
                rows={4}
                className="w-full p-4 text-sm text-[#1E293B] dark:text-white border-2 border-[#F1F5F9] dark:border-gray-800 dark:bg-black rounded-2xl focus:outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-[#3B82F6] transition-all resize-none placeholder:text-[#94A3B8]"
                placeholder="Write your reason or feedback here..."
                value={approverComments}
                onChange={(e) => setApproverComments(e.target.value)}
              />
              <p className="mt-3 text-[11px] italic text-[#94A3B8] dark:text-gray-500 font-medium">* This remark will be visible to the employee.</p>
            </div>

            <div className="px-6 py-5 bg-[#F8FAFC]/50 dark:bg-gray-950/20 border-t border-[#F1F5F9] dark:border-gray-800 flex items-center justify-end gap-6">
              <button onClick={() => { setIsModalOpen(false); setApproverComments(""); setActionType(""); }} className="text-xs font-bold text-[#64748B] hover:text-[#0F172A] dark:hover:text-white transition-colors uppercase">CANCEL</button>
              <button onClick={() => { handleApproveReject(selectedRequest.id, actionType); setIsModalOpen(false); }} className={`px-8 py-3 rounded-xl text-white font-bold transition-all shadow-xl uppercase transform hover:scale-[1.02] active:scale-[0.98] text-xs ${actionType === "approved" ? "bg-[#10B981] hover:bg-[#059669]" : "bg-[#EF4444] hover:bg-[#DC2626]"}`}>{actionType === "approved" ? "APPROVED" : "REJECTED"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const DetailCard = ({ label, value, icon }) => (
  <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow">
    <div className="flex items-start gap-3">
      <div className="mt-0.5">{icon}</div>
      <div className="flex-1">
        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">{label}</p>
        <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">{value || '-'}</p>
      </div>
    </div>
  </div>
);

export default STOApproval;
